import { authenticatedFetch, authenticatedPost, authenticatedPatch, authenticatedDelete } from "../utils/apiUtils";
import { logDebug, logError, logWarn } from "../utils/logging";
import { fetchWithRetry, ApiClientError } from "../utils/fetchWithRetry";
import { getApiBaseUrl } from "./configService";
import { AppError, toAppError } from "../types/errors";
import { SmartiePlan } from "../types";
import { createResilientSSE } from "./sseClient";

// TypeScript interfaces for v2 API responses
export interface Mindspace {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContextItem {
  id: string;
  mindspace_id: string;
  item_type: string; // Canonical key
  content: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface QuickActionTemplate {
  id: string;
  template_id: string;
  title: string;
  description: string;
  category: "copywriting" | "documente" | "altele" | "strategic";
  card_type: "creaza_draft" | "sfaturi_strategice";
  initial_prompt: string;
  questions: Array<{
    text: string;
    canonical_key?: string;
    template_key?: string;
    tier?: number;
    is_canonical: boolean;
  }>;
  final_prompt_template: string;
  implicit_context_keys?: string[];
}

// Normalize helpers for defensive shaping of API responses
const isGuidedQuestionArray = (
  value: unknown,
): value is QuickActionTemplate["questions"] => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (q) =>
      typeof q === "object" &&
      q !== null &&
      typeof (q as { text?: unknown }).text === "string",
  );
};

const normalizeTemplate = (tpl: QuickActionTemplate): QuickActionTemplate => {
  const rawQuestions: unknown = (tpl as unknown as { questions?: unknown })
    ?.questions;
  const questions = isGuidedQuestionArray(rawQuestions) ? rawQuestions : [];
  return {
    ...tpl,
    questions,
    final_prompt_template:
      typeof tpl.final_prompt_template === "string"
        ? tpl.final_prompt_template
        : "",
    initial_prompt:
      typeof tpl.initial_prompt === "string" ? tpl.initial_prompt : "",
  };
};

const normalizeTemplates = (
  list: QuickActionTemplate[],
): QuickActionTemplate[] => list.map(normalizeTemplate);

export interface CreateContextItemRequest {
  item_type: string;
  content: string;
  metadata?: Record<string, unknown>;
}

export interface ChatStreamRequest {
  session_id?: string;
  message: string;
  template_id?: string;
  context_override?: Record<string, unknown>;
}

export type UrlFetchStatus = "succeeded" | "failed" | "empty";

export interface ChatStreamUnifiedRequest extends ChatStreamRequest {
  mindspace_id?: string;
  session_context?: Record<string, unknown>;
  url_content?: string;
  url_fetch_status?: UrlFetchStatus;
  attempted_url?: string;
  url_fetch_error?: string;
  message_metadata?: Record<string, unknown>;
}

export interface SessionResponse {
  session_id: string;
  title: string;
  updated_at: string;
}

/**
 * Analyze a URL to extract its text content.
 *
 * Bounded by a client-side timeout so a slow/dead upstream can't hang the send
 * flow — the caller continues with `url_fetch_status = "failed"`. Matches the
 * backend's `_ANALYZE_URL_TIMEOUT_S` so client and server share one upper bound.
 */
const ANALYZE_URL_TIMEOUT_MS = 15_000;
export async function analyzeUrl(urlToAnalyze: string): Promise<{ content: string }> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/tools/analyze-url`;

  logDebug("[apiClientV2] Analyzing URL:", urlToAnalyze);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ANALYZE_URL_TIMEOUT_MS);
  try {
    return await authenticatedPost<{ content: string }>(
      url,
      { url: urlToAnalyze },
      "apiClientV2",
      "analyzeUrl",
      { signal: controller.signal },
    );
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") {
      throw toAppError(
        `URL fetch timed out after ${ANALYZE_URL_TIMEOUT_MS / 1000}s`,
        504,
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Improve a user's prompt using AI.
 * Takes a draft prompt and returns an enhanced, more detailed version.
 */
export async function improvePrompt(prompt: string): Promise<{ improved_prompt: string }> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/prompt-improver`;

  logDebug("[apiClientV2] Improving prompt. Input length:", prompt.length);

  return authenticatedPost<{ improved_prompt: string }>(
    url,
    { prompt },
    "apiClientV2",
    "improvePrompt",
  );
}

/**
 * Get user's mindspaces (auto-creates default mindspace on first call)
 */
export async function fetchMindspaces(): Promise<Mindspace[]> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/`;

  logDebug("[apiClientV2] Fetching mindspaces from:", url);

  return authenticatedFetch<Mindspace[]>(url, "apiClientV2", "fetchMindspaces");
}

/**
 * Get specific mindspace by ID
 */
export async function fetchMindspace(mindspaceId: string): Promise<Mindspace> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}`;

  logDebug("[apiClientV2] Fetching mindspace:", mindspaceId);

  return authenticatedFetch<Mindspace>(url, "apiClientV2", "fetchMindspace");
}

/**
 * Create a new mindspace
 */
export async function createMindspace(
  name: string,
  description?: string,
): Promise<Mindspace> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/`;

  logDebug("[apiClientV2] Creating mindspace:", name);

  return authenticatedPost<Mindspace>(
    url,
    { name, description },
    "apiClientV2",
    "createMindspace",
  );
}

/**
 * Update an existing mindspace
 */
export async function updateMindspace(
  mindspaceId: string,
  updates: { name?: string; description?: string },
): Promise<Mindspace> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}`;

  logDebug("[apiClientV2] Updating mindspace:", mindspaceId, updates);

  return authenticatedPatch<Mindspace>(
    url,
    updates,
    "apiClientV2",
    "updateMindspace",
  );
}

/**
 * Delete a mindspace
 */
export async function deleteMindspace(mindspaceId: string): Promise<void> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}`;

  logDebug("[apiClientV2] Deleting mindspace:", mindspaceId);

  return authenticatedDelete<void>(url, "apiClientV2", "deleteMindspace");
}

/**
 * Set a mindspace as default
 */
export async function setDefaultMindspace(
  mindspaceId: string,
): Promise<Mindspace> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/set-default`;

  logDebug("[apiClientV2] Setting default mindspace:", mindspaceId);

  return authenticatedPost<Mindspace>(
    url,
    {},
    "apiClientV2",
    "setDefaultMindspace",
  );
}

/**
 * Get context items for a mindspace
 */
export async function fetchContextItems(
  mindspaceId: string,
): Promise<ContextItem[]> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/context-items`;

  logDebug("[apiClientV2] Fetching context items for mindspace:", mindspaceId);

  return authenticatedFetch<ContextItem[]>(
    url,
    "apiClientV2",
    "fetchContextItems",
  );
}

/**
 * Create or update context item (upsert for canonical keys)
 */
export async function createContextItem(
  mindspaceId: string,
  contextItem: CreateContextItemRequest,
): Promise<ContextItem> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/context-items`;

  logDebug(
    "[apiClientV2] Creating/updating context item for mindspace:",
    mindspaceId,
    contextItem,
  );

  return authenticatedPost<ContextItem>(
    url,
    contextItem,
    "apiClientV2",
    "createContextItem",
  );
}

/**
 * Get quick action templates with optional filtering
 */
export async function fetchQuickActionTemplates(params?: {
  category?: "copywriting" | "documente" | "altele" | "strategic";
  card_type?: "creaza_draft" | "sfaturi_strategice";
}): Promise<QuickActionTemplate[]> {
  const API_BASE_URL = getApiBaseUrl("v2");

  // Build query parameters
  const searchParams = new URLSearchParams();
  if (params?.category) {
    searchParams.append("category", params.category);
  }
  if (params?.card_type) {
    searchParams.append("card_type", params.card_type);
  }

  const url = `${API_BASE_URL}/quick-actions/${searchParams.toString() ? "?" + searchParams.toString() : ""}`;

  logDebug("[apiClientV2] Fetching quick action templates:", url, params);

  try {
    const response = await fetchWithRetry(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      retries: 3,
      retryDelay: 1000,
      retryOn: () => true,
    });

    const data = await response.json();
    logDebug("[apiClientV2] Quick actions response:", data);
    return Array.isArray(data) ? normalizeTemplates(data) : [];
  } catch (error) {
    logError("[apiClientV2] Error fetching quick action templates:", error);
    // Re-throw as ApiClientError if network or other unknown
    if (!(error instanceof ApiClientError)) {
      throw new ApiClientError(
        "Failed to fetch quick action templates",
        undefined,
      );
    }
    throw error;
  }
}

export interface ContextKeyDefinition {
  id: string;
  key_name: string;
  display_label: string;
  description?: string;
  category?: string;
  is_persistent: boolean;
  is_active: boolean;
  sort_order: number;
}

/**
 * Get all active context key definitions (public endpoint)
 */
export async function fetchContextKeyDefinitions(): Promise<ContextKeyDefinition[]> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/context-keys/`;

  logDebug("[apiClientV2] Fetching context key definitions:", url);

  try {
    const response = await fetchWithRetry(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    logDebug("[apiClientV2] Context keys response:", data);
    return data as ContextKeyDefinition[];
  } catch (error) {
    logError("[apiClientV2] Error fetching context key definitions:", error);
    if (!(error instanceof ApiClientError)) {
      throw new ApiClientError(
        "Failed to fetch context key definitions",
        undefined,
      );
    }
    throw error;
  }
}

/**
 * Get specific quick action template by ID
 */
export async function fetchQuickActionTemplate(
  templateId: string,
): Promise<QuickActionTemplate> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/quick-actions/${templateId}`;

  logDebug("[apiClientV2] Fetching quick action template:", templateId);

  // Public endpoint, no auth required
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError(
        "[apiClientV2] Quick action template fetch failed:",
        response.status,
        errorText,
      );
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    logDebug("[apiClientV2] Quick action template response:", data);
    return normalizeTemplate(data as QuickActionTemplate);
  } catch (error) {
    logError("[apiClientV2] Error fetching quick action template:", error);
    throw error;
  }
}

/**
 * Stream chat with context (v2 endpoint)
 */
export async function streamChatWithContext(
  mindspaceId: string,
  requestData: ChatStreamRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (error: AppError) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/chat/stream`;

  logDebug(
    "[apiClientV2] Starting context-aware chat stream:",
    mindspaceId,
    requestData,
  );

  const abortController = new AbortController();
  onStreamControllerCreated(abortController);

  try {
    const sse = createResilientSSE({
      url,
      method: "POST",
      body: requestData,
      signal: abortController.signal,
      onOpen: () => {
        logDebug("[apiClientV2] SSE stream opened for context-aware chat.");
      },
      onMessage: (data, eventType) => {
        if (eventType === "token" || !eventType) {
          try {
            logDebug("[apiClientV2] Received token data:", data);
            const parsedData = JSON.parse(data);
            if (parsedData && typeof parsedData.token === "string") {
              onToken(parsedData.token);
            } else {
              logError(
                "[apiClientV2] Invalid token data structure:",
                parsedData,
              );
            }
          } catch (e) {
            logError("[apiClientV2] Failed to parse token data:", e, data);
          }
        }
      },
      onEnd: () => {
        onEnd();
        onStreamControllerCreated(null);
      },
      onError: (err) => {
        const appErr = toAppError(err, 500);
        onError(appErr);
        onStreamControllerCreated(null);
      },
      onStateChange: (state, attempt) => {
        if (state === "reconnecting") {
          logWarn(
            `[apiClientV2] SSE reconnecting (attempt ${attempt}) for context-aware chat`,
          );
        }
      },
    });

    await sse.completed;
  } catch (error) {
    logError("[apiClientV2] Error in streamChatWithContext:", error);
    const appErr = toAppError(error, 500);
    onError(appErr);
    onStreamControllerCreated(null);
    if (!abortController.signal.aborted) {
      abortController.abort();
    }
  }
}

/**
 * Stream chat via unified v2 endpoint (guest-friendly).
 *
 * Behaviour:
 *   - If `mindspace_id` is provided, backend will use it (authenticated users).
 *   - If omitted (guests), backend falls back to session-only context.
 *
 * The caller is expected to build `requestData` with appropriate fields and, for guests,
 * exclude `mindspace_id` while including `session_context` populated from guided flow answers.
 */
export async function streamChat(
  requestData: ChatStreamUnifiedRequest,
  onToken: (chunk: string) => void,
  onEnd: (data?: { messageId?: string }) => void,
  onError: (error: AppError) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
  onStatus?: (status: string) => void,
  onPlan?: (plan: SmartiePlan) => void,
): Promise<void> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/chat/stream`;

  logDebug("[apiClientV2] Starting unified chat stream:", requestData);

  const abortController = new AbortController();
  onStreamControllerCreated(abortController);

  try {
    const sse = createResilientSSE({
      url,
      method: "POST",
      body: requestData,
      signal: abortController.signal,
      onOpen: () => {
        logDebug("[apiClientV2] SSE stream opened for unified chat.");
      },
      onMessage: (data, eventType) => {
        if (eventType === "token" || !eventType) {
          try {
            const parsedData = JSON.parse(data);
            if (parsedData && typeof parsedData.token === "string") {
              onToken(parsedData.token);
            } else {
              logError(
                "[apiClientV2] Invalid token data structure:",
                parsedData,
              );
            }
          } catch (e) {
            logError("[apiClientV2] Failed to parse token data:", e, data);
          }
        } else if (eventType === "status" && onStatus) {
          try {
            const parsedData = JSON.parse(data);
            if (parsedData && typeof parsedData.status === "string") {
              onStatus(parsedData.status);
            }
          } catch (e) {
            logError("[apiClientV2] Failed to parse status data:", e, data);
          }
        } else if (eventType === "plan" && onPlan) {
          try {
            const parsedData = JSON.parse(data);
            if (parsedData && parsedData.plan) {
              onPlan(parsedData.plan);
            }
          } catch (e) {
            logError("[apiClientV2] Failed to parse plan data:", e, data);
          }
        }
        // Note: "end" events are handled by createResilientSSE calling onEnd(data)
      },
      onEnd: (data?: string) => {
        // If data is provided, it's from the "end" event containing the message ID
        if (data) {
          try {
            const parsedData = JSON.parse(data);
            onEnd(parsedData);
          } catch (e) {
            logWarn("[apiClientV2] Failed to parse end data, calling onEnd without data:", e);
            onEnd();
          }
        } else {
          // Stream closed without explicit data (or second call after end event)
          // We call onEnd() to ensure cleanup in store if it wasn't called yet
          onEnd();
        }
        onStreamControllerCreated(null);
      },
      onError: (err) => {
        const appErr = toAppError(err, 500);
        onError(appErr);
        onStreamControllerCreated(null);
      },
      onStateChange: (state, attempt) => {
        if (state === "reconnecting") {
          logWarn(
            `[apiClientV2] SSE reconnecting (attempt ${attempt}) for unified chat`,
          );
        }
      },
    });

    await sse.completed;

    // Fallback cleanup
    onStreamControllerCreated(null);

  } catch (error) {
    logError("[apiClientV2] Error in streamChat (unified):", error);
    const appErr = toAppError(error, 500);
    onError(appErr);
    onStreamControllerCreated(null);
    if (!abortController.signal.aborted) {
      abortController.abort();
    }
  }
}

/**
 * Submit feedback for a chat message (thumbs up/down).
 *
 * @param messageId The UUID of the chat message.
 * @param value 1 for thumbs up, -1 for thumbs down.
 * @param comment Optional comment.
 * @param metadata Optional metadata.
 */
export async function submitFeedback(
  messageId: string,
  value: 1 | -1,
  comment?: string,
  metadata?: Record<string, unknown>,
): Promise<{
  chat_message_id: string;
  value: 1 | -1;
  comment?: string;
  metadata_?: Record<string, unknown>;
}> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/chat/messages/${messageId}/feedback`;

  logDebug("[apiClientV2] Submitting feedback:", messageId, value, comment);

  const body = {
    value,
    comment,
    metadata_: metadata,
  };

  return authenticatedPost(url, body, "apiClientV2", "submitFeedback");
}

/**
 * Update a chat session's title (rename)
 */
export async function updateSession(
  sessionId: string,
  updateData: { title: string }
): Promise<SessionResponse> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/chat/sessions/${sessionId}`;

  logDebug("[apiClientV2] Updating session title:", sessionId, updateData);

  return authenticatedPatch<SessionResponse>(
    url,
    updateData,
    "apiClientV2",
    "updateSession"
  );
}

/**
 * Delete a chat session
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/chat/sessions/${sessionId}`;

  logDebug("[apiClientV2] Deleting session:", sessionId);

  return authenticatedDelete<void>(url, "apiClientV2", "deleteSession");
}
